import React, { Component } from 'react';
import { any, arrayOf, bool, func, shape, string } from 'prop-types';
import * as d3 from 'd3';
import { getMessage } from '@tcc/i18n';
import { Help } from './Help';
import { container } from './css/BaseChart.css';
import styles from './css/AccumulatedPrecipitation.css';
import { QuantityUnitType } from '../../types';
import {
  generateChartConfig,
  buildLine,
  drawLine,
  drawArea,
  buildArea,
  drawFocusDot,
  formatGraphEntries,
  getWidthForLength,
  buildLocaleObject,
  formatGraphValues,
  getProjections,
} from '../../utils/chartUtils';

const buildLocalePrecipitationObject = (rainfall) =>
  buildLocaleObject(rainfall, 'rainfall');
const checkForHistoricalData = ({ q }) => q.length;
export class AccumulatedPrecipitation extends Component {
  containerWidth = 0;

  componentDidMount() {
    if (this.refContainer) {
      this.containerWidth = this.refContainer.clientWidth;
    }
    this.createAreaChart();
  }

  componentDidUpdate() {
    this.createAreaChart();
  }

  handleMousemove({
    element,
    focus,
    historicalFocus,
    tooltip,
    yScale,
    past,
    historical,
    projectIndex,
  }) {
    const { formattedDates } = this.props;
    const mouseIndex = d3.mouse(element)[0];
    const dataIndex = projectIndex(mouseIndex);
    const localeGroup = 'rainfall';
    const dataScalar = past.q[dataIndex];
    const historicalScalar = historical.q && historical.q[dataIndex];

    const formattedPrecip = formatGraphEntries(
      dataScalar,
      past.u,
      localeGroup
    ).toString();
    // TODO make `number || number === 0` a util function
    const formattedHistoric =
      historicalScalar || historicalScalar === 0
        ? formatGraphEntries(historicalScalar, past.u, localeGroup).toString()
        : '';
    const pastValueText = getMessage('chartTooltips.accumulatedPrecip', {
      value: formattedPrecip,
      date: formattedDates[dataIndex],
    });
    const historicValueText = historicalScalar
      ? getMessage('chartTooltips.yearsAveraged', { value: formattedHistoric })
      : '';

    if (formattedPrecip) {
      const topScale = historicalScalar
        ? Math.max(historicalScalar, dataScalar)
        : dataScalar;
      tooltip
        .html(
          `
          <div>${pastValueText}</div>
          <div>${historicValueText}</div>
          <div></div>
          `
        )
        .style('left', `${mouseIndex - 91.5}px`)
        .style('top', `${yScale(topScale) - 60}px`);

      focus.attr(
        'transform',
        `translate(${mouseIndex + 50},${yScale(dataScalar) - 7})`
      );
    }

    if (historicalScalar) {
      historicalFocus.attr(
        'transform',
        `translate(${mouseIndex + 50},${yScale(historicalScalar) - 7})`
      );
    }
  }

  createAreaChart = () => {
    const { accumulatedPrecipitation, startDate, endDate } = this.props;
    const { historical, past } = accumulatedPrecipitation;
    const { u: unit, q: quantities } = buildLocalePrecipitationObject(past);
    const hasHistoricalData = checkForHistoricalData(historical);
    const [lowest, possibleHighest] = d3.extent(quantities);
    const highest = Math.max(
      possibleHighest,
      hasHistoricalData
        ? formatGraphValues(d3.max(historical.q), historical.u, 'rainfall')
        : 0
    );
    const total = quantities.length;
    const { node } = this;
    d3.select(node).selectAll('*').remove();
    const { height, width, yScale, timeDiff } = generateChartConfig(
      node,
      highest,
      lowest,
      total,
      startDate,
      endDate,
      this.containerWidth
    );
    const { projectIndex, projectPosition } = getProjections(width, timeDiff);

    const precipLine = buildLine(projectPosition, yScale);

    const g = d3.select(node).append('g').attr('class', styles.g);

    g.selectAll('path line').remove();

    const area = buildArea(projectPosition, yScale, 250);
    g.append(() => drawLine(quantities, styles.line, precipLine));
    g.append(() => drawArea(quantities, styles.chartArea, area));

    const focus = g.append(() => drawFocusDot(styles.focus));
    const historicalFocus = g.append(() =>
      drawFocusDot(styles.historicalFocus)
    );

    const tooltip = d3.selectAll(`.${styles.tooltip}`).style('display', 'none');

    const { yearAverage } = this.props;
    const overlay = g
      .append('rect')
      .attr('class', styles.overlay)
      .attr('width', width)
      .attr('height', height)
      .on('mouseover', () => {
        const numbers = yearAverage.match(/\d+/);
        const numYears = numbers && numbers[0];
        tooltip.style('display', null);
        focus.style('display', null);
        if (numYears) {
          historicalFocus.style('display', null);
        }
      })
      .on('mouseout', () => {
        tooltip.style('display', 'none');
        focus.style('display', 'none');
        historicalFocus.style('display', 'none');
      });

    let handleMousemoveArgs = {
      tooltip,
      yScale,
      focus,
      historicalFocus,
      projectIndex,
      past: { q: quantities, u: unit },
      historical: { q: [] },
    };
    // overlay if value is selected for comparing historical 'years averaged' data
    if (historical.q && historical.q.length > 0) {
      const { q: hQuantities, u: hUnit } =
        buildLocalePrecipitationObject(historical);
      g.append(() => drawLine(hQuantities, styles.historicalLine, precipLine));
      handleMousemoveArgs = {
        ...handleMousemoveArgs,
        historical: { q: hQuantities, u: hUnit },
      };
    }

    const handleMousemove = this.handleMousemove.bind(this);
    overlay.on('mousemove', function onMouseMove() {
      handleMousemove({ element: this, ...handleMousemoveArgs });
    });
  };

  render() {
    const {
      accumulatedPrecipitation: { past },
      precipitationHelp,
      toggleHelp,
    } = this.props;
    const width = getWidthForLength(past.q.length, this.containerWidth) + 50;
    return (
      <div>
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>
            {getMessage('past.accumPrecip.title')}
          </h2>
          <div
            className={styles.help}
            onClick={toggleHelp}
            role="button"
            tabIndex={0}
          >
            {getMessage('past.help')}
            <div>?</div>
          </div>
        </div>

        <Help
          showHelp={precipitationHelp}
          title={getMessage('past.accumPrecip.title')}
          toggle={toggleHelp}
        >
          <li>{getMessage('past.precipHelp.p1')}</li>
          <li>{getMessage('past.precipHelp.p2')}</li>
          <li>{getMessage('past.precipHelp.p3')}</li>
        </Help>

        <div
          className={container}
          ref={(refContainer) => {
            this.refContainer = refContainer;
          }}
        >
          <svg
            className={styles.svg}
            data-test-id="accum-precipitation-svg"
            height={300}
            ref={(node) => {
              this.node = node;
            }}
            width={width}
          />
          <div className={styles.tooltip} />
        </div>
      </div>
    );
  }
}

AccumulatedPrecipitation.propTypes = {
  accumulatedPrecipitation: shape({
    past: QuantityUnitType,
    historical: QuantityUnitType,
  }),
  endDate: any,
  formattedDates: arrayOf(any),
  precipitationHelp: bool,
  startDate: any,
  toggleHelp: func.isRequired,
  yearAverage: string,
};

export default AccumulatedPrecipitation;
